<script setup lang="ts">
/**
 * Contact Thank-You Page — Booking Confirmation
 * Route: /contact/thank-you/
 * Task: 113-112-T002 (fix: 113-112-T004 bugs #1+#2)
 *
 * Renders a booking confirmation message when ?booking=success is present.
 * Emits booking_completed analytics event (Osano-gated, stubbed).
 * Gracefully handles missing or non-success query param with fallback message.
 * Meets WCAG 2.1 AA requirements.
 */

useSeoMeta({
  title: 'Demo Confirmed | QxAIOS',
  description: 'Your demo booking is confirmed. Check your email for details.',
})

const route = useRoute()

const isBookingSuccess = computed(() => route.query.booking === 'success')

const { emitBookingCompleted } = useAnalytics()

// Osano consent check — follows established pattern from contact/index.vue
// Consent management stubbed (deferred per prior spike findings); always true for now
function hasAnalyticsConsent(): boolean {
  // TODO: Integrate real Osano consent check — deferred per qxaiops/pathfinder/112-141/spike-findings-T000.md
  return true
}

onMounted(() => {
  if (isBookingSuccess.value && hasAnalyticsConsent()) {
    emitBookingCompleted(null)
  }
})
</script>

<template>
  <div class="overflow-x-hidden">
    <UContainer class="py-12">
      <div class="max-w-2xl mx-auto">
        <!-- Booking confirmation: shown when ?booking=success -->
        <section
          v-if="isBookingSuccess"
          role="status"
          class="space-y-6"
        >
          <UCard class="text-center">
            <div class="space-y-6">
              <div class="flex justify-center">
                <UIcon
                  name="i-heroicons-check-circle"
                  aria-label="Success"
                  class="w-16 h-16 text-success-500"
                />
              </div>

              <h1 class="text-3xl font-bold text-neutral-900">
                Thank you! Your demo is confirmed.
              </h1>

              <p class="text-lg text-neutral-600">
                We look forward to speaking with you.
              </p>

              <section
                aria-label="Next steps"
                class="text-left space-y-4"
              >
                <h2 class="text-xl font-semibold text-neutral-800">
                  What happens next
                </h2>
                <ul class="space-y-3 text-neutral-700">
                  <li class="flex items-start gap-3">
                    <UIcon
                      name="i-heroicons-envelope"
                      class="mt-0.5 shrink-0 text-primary-500"
                      aria-hidden="true"
                    />
                    <span>Check your email for booking confirmation and calendar invite</span>
                  </li>
                  <li class="flex items-start gap-3">
                    <UIcon
                      name="i-heroicons-clipboard-document-list"
                      class="mt-0.5 shrink-0 text-primary-500"
                      aria-hidden="true"
                    />
                    <span>Prepare questions about our products for your demo</span>
                  </li>
                  <li class="flex items-start gap-3">
                    <UIcon
                      name="i-heroicons-book-open"
                      class="mt-0.5 shrink-0 text-primary-500"
                      aria-hidden="true"
                    />
                    <span>Review our resources to learn more before your session</span>
                  </li>
                  <li class="flex items-start gap-3">
                    <UIcon
                      name="i-heroicons-calendar-days"
                      class="mt-0.5 shrink-0 text-primary-500"
                      aria-hidden="true"
                    />
                    <span>Our team will reach out before your scheduled time with a meeting link and agenda</span>
                  </li>
                </ul>
              </section>

              <div class="flex flex-col sm:flex-row gap-4 justify-center pt-4">
                <UButton
                  to="/"
                  variant="solid"
                  size="lg"
                  class="min-h-[44px] min-w-[44px]"
                >
                  Back to Home
                </UButton>
                <UButton
                  to="/products/"
                  variant="outline"
                  size="lg"
                  class="min-h-[44px] min-w-[44px]"
                >
                  Browse Products
                </UButton>
              </div>
            </div>
          </UCard>
        </section>

        <!-- Fallback: shown when booking param is absent or not 'success' -->
        <section
          v-else
          class="space-y-6"
        >
          <UCard class="text-center">
            <div class="space-y-6">
              <h1 class="text-3xl font-bold text-neutral-900">
                Thank you for reaching out
              </h1>

              <p class="text-lg text-neutral-600">
                Your request has been received.
              </p>

              <p class="text-neutral-600">
                A member of our team will be in touch with you shortly.
              </p>

              <div class="flex flex-col sm:flex-row gap-4 justify-center pt-4">
                <UButton
                  to="/"
                  variant="solid"
                  size="lg"
                  class="min-h-[44px] min-w-[44px]"
                >
                  Back to Home
                </UButton>
                <UButton
                  to="/products/"
                  variant="outline"
                  size="lg"
                  class="min-h-[44px] min-w-[44px]"
                >
                  Browse Products
                </UButton>
              </div>
            </div>
          </UCard>
        </section>
      </div>
    </UContainer>
  </div>
</template>
